import React, { useEffect, useRef } from 'react';
import { Phone, Globe, MapPin, Facebook, Instagram, Twitter, Star, Award } from 'lucide-react';
import { Badge } from './ui/badge';
import { trackEvent } from '../lib/api';

export const BusinessCard = ({ business, referrerCategory = null, referrerSubcategory = null }) => {
  const hasTrackedView = useRef(false);

  const categoryImages = {
    'Creative & Event Services': 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'Beauty & Personal Care': 'https://images.unsplash.com/photo-1549663369-22ac6b052faf?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'Food & Dining': 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'Retail & Shopping': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
    'Auto Services': 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  };

  const imageUrl = business.image_url || categoryImages[business.category] || categoryImages['Creative & Event Services'];

  // Track view when card becomes visible
  useEffect(() => {
    if (!hasTrackedView.current && business.id) {
      trackEvent(business.id, 'view', referrerCategory, referrerSubcategory).catch(() => {});
      hasTrackedView.current = true;
    }
  }, [business.id, referrerCategory, referrerSubcategory]);

  // Track outbound clicks
  const handleClick = (eventType) => {
    trackEvent(business.id, eventType, referrerCategory, referrerSubcategory).catch(() => {});
  };

  return (
    <div
      className="business-card group relative overflow-hidden rounded-2xl bg-card border border-border"
      data-testid={`business-card-${business.id}`}
    >
      {/* Founding Business Badge */}
      {business.is_founding && (
        <div className="absolute top-4 left-4 z-10 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
          <Award className="w-3 h-3" />
          Founding Business
        </div>
      )}

      {/* Featured Badge */}
      {business.is_featured && (
        <div className="featured-badge absolute top-4 right-4 z-10 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider text-primary-foreground flex items-center gap-1">
          <Star className="w-3 h-3 fill-current" />
          Featured
        </div>
      )}

      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={business.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="secondary" className="text-xs">
            {business.category}
          </Badge>
          {business.subcategory && (
            <Badge variant="outline" className="text-xs text-primary border-primary/30">
              {business.subcategory}
            </Badge>
          )}
        </div>

        <h3 className="text-xl font-semibold text-foreground mb-2 line-clamp-1">
          {business.name}
        </h3>

        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {business.description}
        </p>

        {/* Contact Info */}
        <div className="space-y-2 mb-4">
          {business.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-4 h-4 text-primary" />
              <a 
                href={`tel:${business.phone}`} 
                className="hover:text-foreground"
                onClick={() => handleClick('click_phone')}
              >
                {business.phone}
              </a>
            </div>
          )}
          {business.address && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary" />
              <span className="line-clamp-1">{business.address}</span>
            </div>
          )}
          {business.website && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Globe className="w-4 h-4 text-primary" />
              <a
                href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground truncate"
                onClick={() => handleClick('click_website')}
              >
                {business.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>

        {/* Social Links */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          {business.facebook && (
            <a
              href={business.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => handleClick('click_facebook')}
              data-testid="social-facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
          )}
          {business.instagram && (
            <a
              href={business.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => handleClick('click_instagram')}
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
          )}
          {business.twitter && (
            <a
              href={business.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => handleClick('click_twitter')}
              data-testid="social-twitter"
            >
              <Twitter className="w-5 h-5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
